// apps/spellingheroweb/src/app/pages/AdminReports.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Button,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AdminNavbar from '../../components/Navbar/AdminNavbar';
import Footer from '../../components/Footer/Footer';

// Icons
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import TimelineIcon from '@mui/icons-material/Timeline';
import DownloadIcon from '@mui/icons-material/Download';
import BookIcon from '@mui/icons-material/Book';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { authen } from '../../firebase/firebase';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

// Brand colors
const BRAND_PRIMARY = '#2A9235';
const BRAND_SECONDARY = '#4CAF50';
const BG_TINT = '#f8fafc';

const AdminReports: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState('Your School');
  const [totalStudents, setTotalStudents] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const db = getFirestore();

    const unsub = onAuthStateChanged(authen, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get admin school info
        const caRef = doc(db, 'custom_auth', user.uid);
        const caSnap = await getDoc(caRef);

        if (caSnap.exists()) {
          const caData = caSnap.data();
          const schoolId = caData?.school_info?.school_id || caData?.school_id;
          const name = caData?.school_info?.name || 'Your School';
          setSchoolName(name);

          if (schoolId) {
            // Get actual student count
            const studentsQuery = query(
              collection(db, 'custom_auth'),
              where('type', '==', 'student'),
              where('school_info.school_id', '==', String(schoolId))
            );
            const studentsSnap = await getDocs(studentsQuery);
            setTotalStudents(studentsSnap.size);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading reports:', error);
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
      >
        <AdminNavbar />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} sx={{ color: BRAND_PRIMARY }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
        }}
      >
        <AdminNavbar />

        <Box sx={{ flex: 1, pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 8 } }}>
          <Container maxWidth="xl">
            {/* Header */}
            <Box sx={{ mb: 4 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Avatar
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    width: 48,
                    height: 48,
                  }}
                >
                  <AssessmentIcon sx={{ color: '#fff', fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: BRAND_PRIMARY,
                    }}
                  >
                    School Reports & Analytics
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {schoolName} - Performance Overview
                  </Typography>
                </Box>
              </Stack>

              {/* Export Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: BRAND_PRIMARY,
                    color: BRAND_PRIMARY,
                    '&:hover': {
                      borderColor: BRAND_PRIMARY,
                      bgcolor: alpha(BRAND_PRIMARY, 0.05),
                    },
                  }}
                >
                  Export Report
                </Button>
              </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: BRAND_PRIMARY,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: BRAND_PRIMARY,
                  },
                }}
              >
                <Tab label="Overview" />
                <Tab label="Student Performance" />
                <Tab label="Activity Trends" />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            {tabValue === 0 && (
              <>
                {/* Key Metrics Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={1}
                      sx={{ height: '100%', bgcolor: '#fff' }}
                    >
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(BRAND_PRIMARY, 0.1),
                              width: 48,
                              height: 48,
                            }}
                          >
                            <GroupsIcon sx={{ color: BRAND_PRIMARY }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h4"
                              sx={{ fontWeight: 900, color: BRAND_PRIMARY }}
                            >
                              {totalStudents}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Students
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={1}
                      sx={{ height: '100%', bgcolor: '#fff' }}
                    >
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(BRAND_SECONDARY, 0.1),
                              width: 48,
                              height: 48,
                            }}
                          >
                            <TrendingUpIcon sx={{ color: BRAND_SECONDARY }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h4"
                              sx={{ fontWeight: 900, color: BRAND_SECONDARY }}
                            >
                              --
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Active Students
                            </Typography>
                            <Chip
                              label="Coming Soon"
                              size="small"
                              sx={{ mt: 0.5, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={1}
                      sx={{ height: '100%', bgcolor: '#fff' }}
                    >
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar
                            sx={{
                              bgcolor: alpha('#FF9800', 0.1),
                              width: 48,
                              height: 48,
                            }}
                          >
                            <TimerIcon sx={{ color: '#FF9800' }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h4"
                              sx={{ fontWeight: 900, color: '#FF9800' }}
                            >
                              --
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Sessions
                            </Typography>
                            <Chip
                              label="Coming Soon"
                              size="small"
                              sx={{ mt: 0.5, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={1}
                      sx={{ height: '100%', bgcolor: '#fff' }}
                    >
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar
                            sx={{
                              bgcolor: alpha('#9C27B0', 0.1),
                              width: 48,
                              height: 48,
                            }}
                          >
                            <EmojiEventsIcon sx={{ color: '#9C27B0' }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h4"
                              sx={{ fontWeight: 900, color: '#9C27B0' }}
                            >
                              --%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Avg. Accuracy
                            </Typography>
                            <Chip
                              label="Coming Soon"
                              size="small"
                              sx={{ mt: 0.5, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  {/* Top Performers */}
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={1}
                      sx={{ height: '100%', bgcolor: '#fff' }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ mb: 2 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(BRAND_PRIMARY, 0.1),
                              width: 40,
                              height: 40,
                            }}
                          >
                            <StarIcon sx={{ color: BRAND_PRIMARY }} />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Top Performers
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 200,
                            textAlign: 'center',
                            bgcolor: alpha(BRAND_PRIMARY, 0.02),
                            borderRadius: 2,
                            p: 3,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(BRAND_PRIMARY, 0.1),
                              width: 60,
                              height: 60,
                              mb: 2,
                            }}
                          >
                            <BuildIcon
                              sx={{ color: BRAND_PRIMARY, fontSize: 30 }}
                            />
                          </Avatar>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, mb: 1 }}
                          >
                            Performance Analytics
                          </Typography>
                          <Typography color="text.secondary" sx={{ mb: 2 }}>
                            Student performance tracking will be available soon
                          </Typography>
                          <Chip
                            label="Coming Soon"
                            sx={{
                              bgcolor: alpha(BRAND_PRIMARY, 0.1),
                              color: BRAND_PRIMARY,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Recent Activity */}
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={1}
                      sx={{ height: '100%', bgcolor: '#fff' }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ mb: 2 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(BRAND_SECONDARY, 0.1),
                              width: 40,
                              height: 40,
                            }}
                          >
                            <TimelineIcon sx={{ color: BRAND_SECONDARY }} />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Recent Activity
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 200,
                            textAlign: 'center',
                            bgcolor: alpha(BRAND_SECONDARY, 0.02),
                            borderRadius: 2,
                            p: 3,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(BRAND_SECONDARY, 0.1),
                              width: 60,
                              height: 60,
                              mb: 2,
                            }}
                          >
                            <BuildIcon
                              sx={{ color: BRAND_SECONDARY, fontSize: 30 }}
                            />
                          </Avatar>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, mb: 1 }}
                          >
                            Activity Tracking
                          </Typography>
                          <Typography color="text.secondary" sx={{ mb: 2 }}>
                            Real-time activity monitoring will be available soon
                          </Typography>
                          <Chip
                            label="Coming Soon"
                            sx={{
                              bgcolor: alpha(BRAND_SECONDARY, 0.1),
                              color: BRAND_SECONDARY,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}

            {tabValue === 1 && (
              <Card
                elevation={1}
                sx={{
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#fff',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(BRAND_PRIMARY, 0.1),
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <BarChartIcon sx={{ color: BRAND_PRIMARY, fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Student Performance Analytics
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Detailed student performance metrics, individual progress
                  tracking, and comparative analysis will be displayed here.
                </Typography>
                <Chip
                  label="Coming Soon"
                  sx={{
                    bgcolor: alpha(BRAND_PRIMARY, 0.1),
                    color: BRAND_PRIMARY,
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                />
              </Card>
            )}

            {tabValue === 2 && (
              <Card
                elevation={1}
                sx={{
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#fff',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(BRAND_SECONDARY, 0.1),
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <TimelineIcon sx={{ color: BRAND_SECONDARY, fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Activity Trends & Insights
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Weekly and monthly activity trends, engagement patterns, and
                  learning insights will be shown here.
                </Typography>
                <Chip
                  label="Coming Soon"
                  sx={{
                    bgcolor: alpha(BRAND_SECONDARY, 0.1),
                    color: BRAND_SECONDARY,
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                />
              </Card>
            )}
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default AdminReports;
